import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, MapPin, CalendarDays } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TENDER_TYPES, STATUS_META, fmtMoney } from "@/lib/data";
import { useAuth } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Tenders() {
  const { user } = useAuth();
  const role = user.role;
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const params = {};
    if (typeFilter !== "all") params.tender_type = typeFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    setLoading(true);
    axios.get(`${API}/tenders`, { params }).then((r) => setTenders(r.data)).finally(() => setLoading(false));
  }, [typeFilter, statusFilter]);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0050A0] mb-3">Global Notice Board</p>
          <h1 className="font-serif text-4xl text-slate-900">Tenders</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="filter-type-select" className="w-[150px] bg-white border-slate-300">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TENDER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.value}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status-select" className="w-[170px] bg-white border-slate-300">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {role !== "vendor" && (
            <Link to="/tenders/new" data-testid="tenders-create-btn" className="inline-flex items-center gap-2 bg-[#0050A0] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200">
              <Plus className="w-4 h-4" aria-hidden="true" /> Float Tender
            </Link>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm py-20 text-center">Loading tenders…</p>
      ) : tenders.length === 0 ? (
        <div className="bg-white border border-slate-200 p-20 text-center" data-testid="tenders-empty-state">
          <p className="text-slate-500">No tenders match your filters.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenders.map((t, i) => (
            <Link
              key={t.id}
              to={`/tenders/${t.id}`}
              data-testid={`tender-card-${i}`}
              className="bg-white border border-slate-200 p-6 flex flex-col gap-4 hover:border-[#0050A0] transition-colors duration-200 fade-up"
              style={{ animationDelay: `${Math.min(i * 0.04, 0.4)}s` }}
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-mono text-[11px] text-slate-400">{t.reference_number}</span>
                <span className={`text-[11px] px-2 py-0.5 border shrink-0 ${STATUS_META[t.status]?.cls}`}>{STATUS_META[t.status]?.label}</span>
              </div>
              <div>
                <span className="inline-block text-[11px] font-semibold text-[#0050A0] bg-[#E6F0F9] px-2 py-0.5 mb-2">{t.tender_type} · {t.category}</span>
                <h3 className="font-serif text-lg text-slate-900 leading-snug line-clamp-2">{t.title}</h3>
              </div>
              <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed">{t.description}</p>
              <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" />{t.country}</span>
                <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />{t.submission_deadline}</span>
                <span className="font-mono font-medium text-slate-700">{fmtMoney(t.estimated_value, t.currency)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
